import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { Modal } from 'bootstrap';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from "primereact/toast";
import "../../../styles/userDetails/userDetails.css";
import userDetailsStyles from "../../../styles/userDetails/userDetails.module.css";
import editUserDetailsClient from '../../../utility/features/api_clients/userDetails/editUserDetailsClient';
import getBranchesList from '../../../utility/features/api_clients/reports/getBranchesList';


function EditUserModal({ userDetails, setIsUserEdited, setLoading, setSuccessMessage, setErrorMessage, openSuccessModal, openFailureModal }) {
    // Initial form state
    const initialFormState = {
        firstName: "",
        lastName: "",
        userName: "",
        email: "",
        password: "",
        confirmPassword: "",
        branchID: [],
        roleID: 0,
        isActive: true
    };
    const [form, setForm] = useState(initialFormState);
    const editUserFormRef = useRef(null);
    const loadingImageRef = useRef(null);
    const successToastRef = useRef(null);
    const errorToastRef = useRef(null);
    const [selectedBranch, setSelectedBranch] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(true);
    const [touchedFields, setTouchedFields] = useState({});

    // Memoized callbacks for MultiSelect to prevent re-renders
    const optionLabelCallback = useCallback((option) => 
        `${option.branchID || option.id}${option.branchName ? ' - ' + option.branchName : ''}`,
        []
    );

    const optionValueCallback = useCallback((option) => 
        option.branchID || option.id,
        []
    );

    const itemTemplateCallback = useCallback((option) => {
        const branchId = option.branchID || option.id;
        const highlight = Array.isArray(userDetails?.branchIDs) && userDetails.branchIDs.includes(branchId);
        return (
            <span style={highlight ? { background: '#ffe066', fontWeight: 'bold', borderRadius: '3px', padding: '2px 6px' } : {}}>
                {(option.branchID || option.id) + (option.branchName ? ' - ' + option.branchName : '')}
            </span>
        );
    }, [userDetails?.branchIDs]);

    const multiSelectChangeCallback = useCallback((e) => {
        setSelectedBranch(e.value);
        const branchIds = e.value.length > 0 ? e.value : userDetails?.branchIDs || [];
        setForm(f => ({ ...f, branchID: branchIds }));
        // Hide branch error if it was shown
        if (editUserFormRef.current) {
            editUserFormRef.current.querySelector(".invalid-branch-req")?.classList.remove("show");
            editUserFormRef.current.querySelector(".invalid-branch-other")?.classList.remove("show");
        }
    }, [userDetails?.branchIDs]);

    const cleanupModalArtifacts = () => {
        // Only unlock scroll when no Bootstrap modals are still open
        const openModals = document.querySelectorAll('.modal.show');
        if (openModals.length > 0) return;

        document.body.classList.remove('modal-open');
        // Bootstrap toggles these inline styles; ensure they are cleared.
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
        // Only remove the style attribute if it's now empty.
        if ((document.body.getAttribute('style') || '').trim() === '') {
            document.body.removeAttribute('style');
        }

        // Remove any orphaned backdrops
        document.querySelectorAll('.modal-backdrop').forEach((bd) => bd.remove());
    };

    const showSuccessToast = (detail) => {
        successToastRef.current?.show({ severity: "success", summary: "Success", detail, life: 3000 });
    };

    const showErrorToast = (detail) => {
        errorToastRef.current?.show({ severity: "error", summary: "Error", detail, life: 5000 });
    };

    // Fetch branches on mount
    useEffect(() => {
        async function fetchBranches() {
            setLoadingBranches(true);
            try {
                const response = await getBranchesList();
                if (response && !response.error && response.data) {
                    setBranches(response.data);
                } else {
                    setBranches([]);
                }
            } catch (e) {
                setBranches([]);
            } finally {
                setLoadingBranches(false);
            }
        }
        fetchBranches();
    }, []);

    // Helper to populate form from userDetails and clear errors
    const hideAllErrors = () => {
        if (editUserFormRef.current) {
            [
                ".invalid-fname-req", ".invalid-fname-other", ".invalid-lname-req", ".invalid-lname-other", ".invalid-userName-req", ".invalid-email-req", ".invalid-email-other", ".invalid-password-req", ".invalid-password-other", ".invalid-confirmPassword-req", ".invalid-confirmPassword-other", ".invalid-confirmPassword-mismatch", ".invalid-branch-req", ".invalid-branch-other"
            ].forEach(cls => {
                const el = editUserFormRef.current.querySelector(cls);
                if (el) el.classList.remove("show");
            });
        }
    };
    const populateFormFromUserDetails = () => {
        if (userDetails) {
            const userBranchId = userDetails.branchIDs ?? userDetails.branchIds ?? userDetails.BranchID ?? [];
            const resolvedFirstName = userDetails.firstName ?? userDetails.FirstName ?? userDetails.firstname ?? "";
            const resolvedLastName = userDetails.lastName ?? userDetails.LastName ?? userDetails.lastname ?? "";
            const resolvedUserName = userDetails.userName ?? userDetails.UserName ?? userDetails.username ?? "";
            const resolvedEmail = userDetails.email ?? userDetails.Email ?? "";
            const resolvedRoleId = Number(userDetails.roleID ?? userDetails.roleId ?? userDetails.RoleID ?? 0);

            setForm(prev => ({
                ...prev,
                firstName: resolvedFirstName,
                lastName: resolvedLastName,
                userName: resolvedUserName,
                email: resolvedEmail,
                roleID: Number.isFinite(resolvedRoleId) ? resolvedRoleId : 0,
                branchID: Array.isArray(userBranchId) ? userBranchId : [],
                isActive: typeof userDetails.isActive === "boolean" ? userDetails.isActive : (typeof userDetails.IsActive === "boolean" ? userDetails.IsActive : prev.isActive),
                // Do not prefill password fields when editing; user should only type here to change password.
                password: "",
                confirmPassword: ""
            }));

            // Set selected branches
            const matches = branches.filter((b) => {
                const branchId = Number(b.branchID ?? b.id ?? 0);
                return Array.isArray(userBranchId) && userBranchId.includes(branchId);
            });
            const selectedBranchIds = matches.map((b) => b.branchID ?? b.id).filter((id) => id !== undefined && id !== null);
            setSelectedBranch(selectedBranchIds.length > 0 ? selectedBranchIds : []);
            // Fresh open / refetch: treat fetched values as not user-edited.
            setTouchedFields({});
        } else {
            setForm(initialFormState);
            setSelectedBranch([]);
            setTouchedFields({});
        }
        hideAllErrors();
    };

    // Populate form when userDetails or branches change
    useEffect(() => {
        // Always repopulate form when modal is shown (fixes issue with second open)
        populateFormFromUserDetails();
        // eslint-disable-next-line
    }, [userDetails, branches]);

    // Listen for modal open event to repopulate form
    useEffect(() => {
        const modalEl = document.getElementById('editUserModal');
        if (!modalEl) return;
        const handleShow = () => {
            populateFormFromUserDetails();
        };
        const handleHidden = () => {
            cleanupModalArtifacts();
        };
        modalEl.addEventListener('show.bs.modal', handleShow);
        modalEl.addEventListener('hidden.bs.modal', handleHidden);
        return () => {
            modalEl.removeEventListener('show.bs.modal', handleShow);
            modalEl.removeEventListener('hidden.bs.modal', handleHidden);
        };
    }, [userDetails, branches]);

    // Handle form field changes and hide error for that field
    const handleForm = (e = null, type, value = null) => {
        const val = value === null ? e.target.value : value;
        setForm(f => ({ ...f, [type]: val }));
        if (type === "firstName" || type === "lastName") {
            setTouchedFields(prev => ({ ...prev, [type]: true }));
        }
        // Hide error for this field
        if (editUserFormRef.current) {
            if (type === "firstName") {
                editUserFormRef.current.querySelector(".invalid-fname-req")?.classList.remove("show");
                editUserFormRef.current.querySelector(".invalid-fname-other")?.classList.remove("show");
            } else if (type === "lastName") {
                editUserFormRef.current.querySelector(".invalid-lname-req")?.classList.remove("show");
                editUserFormRef.current.querySelector(".invalid-lname-other")?.classList.remove("show");
            } else if (type === "userName") {
                editUserFormRef.current.querySelector(".invalid-userName-req")?.classList.remove("show");
            } else if (type === "email") {
                editUserFormRef.current.querySelector(".invalid-email-req")?.classList.remove("show");
                editUserFormRef.current.querySelector(".invalid-email-other")?.classList.remove("show");
            } else if (type === "password") {
                editUserFormRef.current.querySelector(".invalid-password-req")?.classList.remove("show");
                editUserFormRef.current.querySelector(".invalid-password-other")?.classList.remove("show");
                // Also hide confirm password mismatch if user is editing password
                editUserFormRef.current.querySelector(".invalid-confirmPassword-mismatch")?.classList.remove("show");
            } else if (type === "confirmPassword") {
                editUserFormRef.current.querySelector(".invalid-confirmPassword-req")?.classList.remove("show");
                editUserFormRef.current.querySelector(".invalid-confirmPassword-other")?.classList.remove("show");
                editUserFormRef.current.querySelector(".invalid-confirmPassword-mismatch")?.classList.remove("show");
            } else if (type === "branchID") {
                editUserFormRef.current.querySelector(".invalid-branch-req")?.classList.remove("show");
                editUserFormRef.current.querySelector(".invalid-branch-other")?.classList.remove("show");
            }
        }
    };

    // Validation logic extracted for clarity
    const validateForm = () => {
        const errors = {};
        // Only enforce strict name validation when the user edits the field.
        // Keeps legacy data editable even if it contains unexpected characters.
        const nameRegex = /^[A-Za-z]+(?:[ .\'-][A-Za-z]+)*$/;
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        const normalizedFirstName = String(form.firstName ?? "").trim().replace(/\s+/g, " ");
        const normalizedLastName = String(form.lastName ?? "").trim().replace(/\s+/g, " ");

        if (!normalizedFirstName) errors.firstName = "First Name is required";
        else if (touchedFields.firstName && !nameRegex.test(normalizedFirstName)) errors.firstName = "Invalid first name";

        if (!normalizedLastName) errors.lastName = "Last Name is required";
        else if (touchedFields.lastName && !nameRegex.test(normalizedLastName)) errors.lastName = "Invalid last name";

        if (!form.userName || String(form.userName).trim() === "") errors.userName = "Username is required";

        if (!form.email) errors.email = "Email is required";
        else if (!emailRegex.test(String(form.email).trim())) errors.email = "Invalid email address";

        const passwordProvided = Boolean(form.password && String(form.password).trim() !== "");
        const confirmPasswordProvided = Boolean(form.confirmPassword && String(form.confirmPassword).trim() !== "");
        // Password is optional on edit; validate only when user is trying to change it.
        if (passwordProvided || confirmPasswordProvided) {
            if (!passwordProvided) errors.password = "Password is required";
            if (!confirmPasswordProvided) errors.confirmPassword = "Confirm password is required";
            else if (form.password !== form.confirmPassword) errors.confirmPassword = "Passwords do not match";
        }

        if (!form.branchID || form.branchID === 0) errors.branchID = "Branch is required";

        return errors;
    };

    // Submit handler
    const editUserFormSubmit = async (event) => {
        event.preventDefault();
        const errors = validateForm();
        // Show errors in UI (legacy: still using DOM for now)
        hideAllErrors();
        if (editUserFormRef.current) {
            // Show errors
            if (errors.firstName) editUserFormRef.current.querySelector(errors.firstName === "First Name is required" ? ".invalid-fname-req" : ".invalid-fname-other").classList.add("show");
            if (errors.lastName) editUserFormRef.current.querySelector(errors.lastName === "Last Name is required" ? ".invalid-lname-req" : ".invalid-lname-other").classList.add("show");
            if (errors.userName) editUserFormRef.current.querySelector(".invalid-userName-req").classList.add("show");
            if (errors.email) editUserFormRef.current.querySelector(errors.email === "Email is required" ? ".invalid-email-req" : ".invalid-email-other").classList.add("show");
            if (errors.password) editUserFormRef.current.querySelector(".invalid-password-req").classList.add("show");
            if (errors.confirmPassword) {
                if (errors.confirmPassword === "Confirm password is required") editUserFormRef.current.querySelector(".invalid-confirmPassword-req").classList.add("show");
                else if (errors.confirmPassword === "Passwords do not match") editUserFormRef.current.querySelector(".invalid-confirmPassword-mismatch").classList.add("show");
                else editUserFormRef.current.querySelector(".invalid-confirmPassword-other").classList.add("show");
            }
            if (errors.branchID) editUserFormRef.current.querySelector(".invalid-branch-req").classList.add("show");
        }
        if (Object.keys(errors).length > 0) return false;

        // Prepare user object
        const passwordToSend = String(form.password ?? "").trim();
        const normalizedFirstName = String(form.firstName ?? "").trim().replace(/\s+/g, " ");
        const normalizedLastName = String(form.lastName ?? "").trim().replace(/\s+/g, " ");
        const editUser = {
            UserName: form.userName.trim(),
            FirstName: normalizedFirstName,
            LastName: normalizedLastName,
            email: form.email.trim(),
            roleID: form.roleID,
            branchIDs: form.branchID,
            IsActive: form.isActive
        };
        if (passwordToSend) {
            editUser.password = passwordToSend;
        }

        setLoading(true);
        try {
            const result = await editUserDetailsClient(editUser);
            if (result.status === 200 && result.data.messageCode === 200) {
                setIsUserEdited(true);
                openSuccessModal?.();
                setErrorMessage('');
                setSuccessMessage("Succesfully updated!");
                showSuccessToast("User Updated Succesfully!");
                handleResetForm();
                setTimeout(() => {
                    const el = document.getElementById('editUserModal');
                    if (el) {
                        const instance = Modal.getOrCreateInstance(el);
                        instance.hide();
                    }
                }, 100);
                cleanupModalArtifacts();
            } else if (result.data.messageCode === 20000) {
                setSuccessMessage("");
                showErrorToast(result?.data?.message);
                setErrorMessage(result?.data?.message);
            }
            else {
                const errorMsg = result?.data?.message || "Failed to edit user";
                setErrorMessage("Failed to edit user");
                showErrorToast("Action failed! Please try again.");
                 setTimeout(() => {
                    const el = document.getElementById('editUserModal');
                    if (el) {
                        const instance = Modal.getOrCreateInstance(el);
                        instance.hide();
                    }
                }, 100);
                cleanupModalArtifacts();
            }
        } catch (err) {
            const errorMsg = err?.message || "Failed to edit user";
            setErrorMessage(errorMsg);
            showErrorToast(errorMsg);
            openFailureModal();
        }
        setLoading(false);
    };

    // Reset form handler
    const handleResetForm = () => {
        if (editUserFormRef.current) editUserFormRef.current.reset();
        if (loadingImageRef.current) loadingImageRef.current.style.display = "none";
        setForm(initialFormState);
        setSelectedBranch([]);
        setTouchedFields({});
        // Hide all errors
        if (editUserFormRef.current) {
            [".invalid-fname-req", ".invalid-fname-other", ".invalid-lname-req", ".invalid-lname-other", ".invalid-userName-req", ".invalid-email-req", ".invalid-email-other", ".invalid-password-req", ".invalid-password-other", ".invalid-confirmPassword-req", ".invalid-confirmPassword-other", ".invalid-confirmPassword-mismatch", ".invalid-branch-req", ".invalid-branch-other"].forEach(cls => {
                const el = editUserFormRef.current.querySelector(cls);
                if (el) el.classList.remove("show");
            });
        }
    };

    return (
        <>
            <Toast ref={successToastRef} position="top-right" />
            <div className={`modal fade ${userDetailsStyles["userDetailsModal"]}`} id="editUserModal" tabIndex="-1" aria-labelledby="editUserModalLabel" aria-hidden="true">
                <div className={`modal-dialog ${userDetailsStyles["modal-dialog"]}`}>
                    <div className={`modal-content ${userDetailsStyles["modal-content"]}`}>
                        <div className={`modal-header ${userDetailsStyles["modal-header"]}`}>
                            <h5 id="editUserModalLabel" className={`modal-title ${userDetailsStyles["modal-title"]}`}>Edit User Details</h5>
                            <button type="button" className={`btn-close ${userDetailsStyles["btn-close"]}`} data-bs-dismiss="modal" aria-label="Close"
                                onClick={() => handleResetForm()} />
                        </div>
                        <Toast ref={errorToastRef} position="top-right" />
                        <div className={`modal-body ${userDetailsStyles["modal-body"]}`}>
                            <p><span> * Required information</span></p>
                            <form autoComplete="off" ref={editUserFormRef} className={`${userDetailsStyles["lead-form"]}`} noValidate="" id="editUserModalForm" name="editUserModal">
                                <div className="form-wrapper">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <label htmlFor="userName" className={`${userDetailsStyles["form-label"]}`}>Username* : </label>
                                            <span htmlFor="userName" className={`${userDetailsStyles["form-label"]}`} name="userName" id="userName"> {form.userName ?? ""}</span>
                                            <div className={`invalid-userName-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} > Username is required</div>

                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <label htmlFor="fname" className={`${userDetailsStyles["form-label"]}`}>First Name*</label>
                                            <input type="text" className={`form-control `} placeholder="Enter firstname" name="fname" id="fname" required="" value={form.firstName} onChange={(e) => handleForm(e, "firstName")} />
                                            <div className={`invalid-fname-req  invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} > First Name is required</div>
                                            <div className={`invalid-fname-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} >Invalid first name</div>
                                        </div>
                                        <div className="col-md-6 tg-f-em">
                                            <label htmlFor="lname" className={`${userDetailsStyles["form-label"]}`}>Last Name*</label>
                                            <input type="text" className={`form-control `} placeholder="Enter lastname" name="lname" id="lname" required="" value={form.lastName} onChange={(e) => handleForm(e, "lastName")} />
                                            <div className={`invalid-lname-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} > Last Name is required</div>
                                            <div className={`invalid-lname-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} >Invalid last name</div>
                                        </div>
                                    </div>
                                    <div className="row mt-4">
                                        <div className="col-md-6">
                                            <label htmlFor="password" className={`${userDetailsStyles["form-label"]}`}>Password</label>
                                            <input type="password"
                                                className={`form-control`}
                                                placeholder="Enter password" id="password" name="password" autoComplete="off" value={form.password} onChange={(e) => handleForm(e, "password")} />
                                            <div className={`invalid-password-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} > Password is required</div>
                                            <div className={`invalid-password-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} > Please enter a valid password</div>
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="confirmPassword" className={`${userDetailsStyles["form-label"]}`}>Confirm Password</label>
                                            <input type="password" className={`form-control`} placeholder="Enter password" id="confirmPassword" name="confirmPassword" autoComplete="off" value={form.confirmPassword} onChange={(e) => handleForm(e, "confirmPassword")} />
                                            <div className={`invalid-confirmPassword-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} >Password is required</div>
                                            <div className={`invalid-confirmPassword-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} > Please enter a valid password</div>
                                            <div className={`invalid-confirmPassword-mismatch invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} > Entered passwords do not match</div>
                                        </div>
                                    </div>
                                    <div className="row mt-4">
                                        <div className="col-md-6">
                                            <label htmlFor="email" className={`${userDetailsStyles["form-label"]}`}>Email*</label>
                                            <input type="text" className={`form-control `} placeholder="Enter email" id="email" name="email" required="" autoComplete="off" value={form.email} onChange={(e) => handleForm(e, "email")} />
                                            <div className={`invalid-email-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} > Email Address is required</div>
                                            <div className={`invalid-email-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`} > Please enter a valid email address e.g. john.doe@gmail.com</div>
                                        </div>
                                        <div className="col-md-6">
                                            <label htmlFor="status" className={`${userDetailsStyles["form-label"]}`}>Status*</label>
                                            <select
                                                id="status"
                                                name="branch"
                                                className={`form-select form-control ${userDetailsStyles["statusSelect"]}`}
                                                required
                                                value={form.isActive}
                                                onChange={(e) =>{
                                                    
                                                     handleForm(null, "isActive", e.target.value === "true")
                                                }
                                                    }
                                            >
                                                <option value="true">Active</option>
                                                <option value="false">Inactive</option>
                                            </select>
                                            <div className={`invalid-status-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Status is required</div>
                                            <div className={`invalid-status-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Please select a valid status</div>
                                        </div>
                                    </div>
                                    <div className="row mt-4">

                                        <div className="col-md-6">
                                            <label htmlFor="branch-typeahead" className={`${userDetailsStyles["form-label"]}`}>Branch*</label>
                                            <MultiSelect
                                                id="branch-typeahead"
                                                value={selectedBranch}
                                                options={branches}
                                                optionLabel={optionLabelCallback}
                                                optionValue={optionValueCallback}
                                                onChange={multiSelectChangeCallback}
                                                placeholder={loadingBranches ? 'Loading branches...' : 'Select or search branch...'}
                                                disabled={loadingBranches}
                                                className="w-100 tg-multiselect"
                                                maxSelectedLabels={2}
                                                showSelectAll={true}
                                                selectAllLabel="Select All"
                                                itemTemplate={itemTemplateCallback}
                                                virtualScroll
                                                scrollHeight="300px"
                                                panelClassName="tg-multiselect-panel"
                                            />
                                            <div className={`invalid-branch-req invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Branch is required</div>
                                            <div className={`invalid-branch-other invalid-feedback ${userDetailsStyles["invalid-feedback"]}`}>Please select a valid branch</div>
                                        </div>
                                    </div>

                                </div>
                                <div className="row mt-4 mb-2">
                                    <div className="col-md-5">
                                        <div id="loading-image" ref={loadingImageRef} style={{ "display": "none", "textAlign": "center" }}>Processing...<img height="35"
                                            width="35"
                                            src={"/images/loading-animation.gif"} />
                                        </div>
                                        <button
                                            type="button"
                                            className={`btn btn-info  ${userDetailsStyles["bg-green"]}`}
                                            onClick={(e) => editUserFormSubmit(e)}
                                        >
                                            Update
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}


export default EditUserModal;
